import type { ChangeEvent } from 'react';

import { DEFAULT_COUNTRY_PHONE_CODE, CONTACT_CENTER_NUMBER } from '../constants';
import type { SnakeToCamelReturnType, UserInfo } from 'interfaces';
import { convertToLimaTZ } from './daysjsConfig';

export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

export const formatPhoneNumber = (phoneNumber: string, isMasked = false) => {
  if (!phoneNumber) return null;

  const phoneNumberDigits = phoneNumber.toString().replace(/\D/g, '');
  let formatted: string | null = null;

  if (phoneNumberDigits.length === 9) {
    const match = phoneNumberDigits.match(/^(\d{3})(\d{3})(\d{3})$/);
    formatted = match ? `${match[1]} ${match[2]} ${match[3]}` : null;
  } else if (phoneNumberDigits.length === 11) {
    // TODO: fix
    const match = phoneNumberDigits.match(/^(\d{2})(\d{3})(\d{3})(\d{3})$/);
    formatted = match ? `+${match[1]} ${match[2]} ${match[3]} ${match[4]}` : null;
  } else {
    formatted = phoneNumberDigits || null;
  }

  if (!formatted) return null;
  if (!isMasked) return formatted;

  let visibleDigits = 2;
  let result = '';

  for (let i = formatted.length - 1; i >= 0; i--) {
    const digit = formatted[i];
    if (/\d/.test(digit)) {
      if (visibleDigits > 0) {
        result = digit + result;
        visibleDigits -= 1;
      } else {
        result = '*' + result;
      }
    } else {
      result = digit + result;
    }
  }

  return result;
};

export const formatLandlinePhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return;
  }

  const phoneNumberDigits = phoneNumber.toString().replace(/\D/g, '');
  return `(${phoneNumberDigits.slice(0, 2)}) ${phoneNumberDigits.slice(2, 5)} ${phoneNumberDigits.slice(5)}`;
};

export const getFullFormattedNumber = (phoneNumber: string | undefined, isMasked = false): string => {
  if (phoneNumber) {
    if (phoneNumber.length === 2) {
      return `${DEFAULT_COUNTRY_PHONE_CODE ?? ''} XXX XXX X${phoneNumber}`;
    } else if (phoneNumber.startsWith(DEFAULT_COUNTRY_PHONE_CODE)) {
      return formatPhoneNumber(phoneNumber, isMasked) ?? '';
    } else if (phoneNumber.startsWith('51')) {
      const trimmedPhone = phoneNumber.slice(2);
      return formatPhoneNumber(trimmedPhone, isMasked) ?? '';
    } else {
      return `${DEFAULT_COUNTRY_PHONE_CODE ?? ''} ${formatPhoneNumber(phoneNumber, isMasked) ?? ''}`;
    }
  }

  return '';
};

export const getCallCenterLink = () => {
  const formattedPhone = formatLandlinePhoneNumber(CONTACT_CENTER_NUMBER);
  const phone = formattedPhone ? `${DEFAULT_COUNTRY_PHONE_CODE} ${formattedPhone}` : null;
  return { shortPhone: CONTACT_CENTER_NUMBER, fullPhone: phone, tel: CONTACT_CENTER_NUMBER };
};

export const secondsToTime = (duration: number) => {
  const hours = ~~(duration / 3600);
  const minutes = ~~((duration % 3600) / 60);
  const seconds = ~~duration % 60;

  let time = '';

  if (hours > 0) {
    time += `${hours}:${minutes < 10 ? '0' : ''}`;
  }

  time += `${minutes}:${seconds < 10 ? '0' : ''}`;
  time += `${seconds}`;

  return time;
};

export const trimDigits = (event: ChangeEvent<HTMLInputElement>) => {
  const value = event?.target?.value ?? '';
  return value.replace(/[0-9]/g, '');
};

export const allowOnlyLetters = (event: ChangeEvent<HTMLInputElement>) => {
  const value = event?.target?.value ?? '';
  if (!/^[a-zA-ZÁáÉéÍíÓóÚúÜüÑñ]+(( |-)[a-zA-ZÁáÉéÍíÓóÚúÜüÑñ]*)*$/.test(value)) {
    return value.slice(0, -1);
  }
  return value;
};

export const allowOnlyDigits = (event: ChangeEvent<HTMLInputElement>) => {
  const value = event?.target?.value ?? '';
  if (!/^[0-9]*$/.test(value)) {
    return value.slice(0, -1);
  }
  return value;
};

export const stringToBankAccountFormat = (account?: string | null) => {
  if (account) {
    const splitted = account.split('');
    splitted.splice(3, 0, '-');
    splitted.splice(7, 0, '-');
    splitted.splice(20, 0, '-');
    return splitted.join('');
  }
};

export const snakeToCamel: (str: string) => SnakeToCamelReturnType = str =>
  str.toLowerCase().replace(/([-_][a-z])/g, group => group.toUpperCase().replace('-', '').replace('_', ''));

export const checkCountryCode = (str: string) => {
  const number = str.replace(/[\s\u202a]+/g, '');

  const clearNumber = number.startsWith(DEFAULT_COUNTRY_PHONE_CODE)
    ? number.replace(DEFAULT_COUNTRY_PHONE_CODE, '')
    : number;
  return clearNumber.replace(/[^0-9]/g, '');
};

export const normalizePhoneDigitsForCompare = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  let digits = checkCountryCode(value);
  if (digits.length === 11 && digits.startsWith('51')) {
    digits = digits.slice(2);
  }
  return digits;
};

export const getFormattedPhoneValue = (targetValue: string) => {
  const value = targetValue.replace(/ /g, '');

  if (value.length >= 6) {
    targetValue = `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6)}`;
  } else if (value.length >= 3) {
    targetValue = `${value.slice(0, 3)} ${value.slice(3)}`;
  } else {
    targetValue = value;
  }
  return targetValue;
};

export const formatDate = (
  date: string | undefined,
  long = false,
  customFormat = { dateF: 'dddd L', timeF: 'h:mm A' },
  hasDash = true,
  format = 'ddd DD.MM.YYYY',
) => {
  if (long && date) {
    const { dateF, timeF } = customFormat;
    const datePart = convertToLimaTZ(date).format(dateF);
    const timePart = convertToLimaTZ(date).format(timeF);
    return `${datePart} ${hasDash ? '-' : ''} ${timePart}`;
  }
  return date ? convertToLimaTZ(date).format(format) : date;
};

export const formatDateShort = (date: string | undefined, dottedFormat = false, withTime = false) => {
  const formatting = dottedFormat ? 'DD.MM.YYYY' : DEFAULT_DATE_FORMAT;

  return date ? convertToLimaTZ(date).format(withTime ? 'DD.MM.YYYY HH:mm' : formatting) : date;
};

export const isUserInBlockList = (date: Date | null | undefined) => {
  return convertToLimaTZ(date).diff(convertToLimaTZ(), 'years') >= 2;
};

const projectNametoASCII = () => {
  const projectName = process.env.REACT_APP_PROJECT_NAME;

  const accents: Record<string, string> = {
    á: 'a',
    é: 'e',
    ó: 'o',
    ú: 'u',
    í: 'i',
    ñ: 'n',
  };

  return projectName
    ?.split('')
    .map((char: string) => accents[char] || char)
    .join('')
    .toLowerCase();
};

export const projectNameId = projectNametoASCII();

export const formatCurrency = (amount: number, hasSymbol = true, minFraction = 2): string => {
  const formatted = new Intl.NumberFormat('es-PE', {
    style: hasSymbol ? 'currency' : 'decimal',
    currency: hasSymbol ? 'PEN' : undefined,
    minimumFractionDigits: minFraction,
    maximumFractionDigits: 2,
  }).format(amount);
  return formatted;
};

export const base64ToArrayBuffer = (base64: string) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const userErrorsToi18n = (message: string) => {
  if (!message) {
    return '';
  }

  const formattedString = message
    .split(' ')
    .map((word, id) => {
      if (id === 0) {
        return word.toLowerCase();
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
    })
    .join('');

  if (formattedString.endsWith('.')) {
    return formattedString.substring(0, formattedString.length - 1);
  } else {
    return formattedString;
  }
};

export const getLabelGAConfirmAndSign = (userInfoData: UserInfo, fromAccount = false) => {
  return userInfoData.has_closed_loans
    ? 'repeat_loan'
    : fromAccount && userInfoData.applications_count && userInfoData.applications_count >= 1
    ? 'repeat_application'
    : 'sign_up';
};

export const formatBankAccountNumber = (accountNumber: string | null) => {
  return !accountNumber
    ? null
    : `${accountNumber.slice(0, 3)}-${accountNumber.slice(3, 6)}-${accountNumber.slice(6, 18)}-${accountNumber.slice(
        18,
      )}`;
};

export const formatToUpperCaseExcept = (string: string) => {
  const exceptions = ['de', 'del', 'of'];

  if (!string) {
    return '';
  }
  const stringLowerCased = string.toLowerCase();
  const wordsArray = stringLowerCased
    .split(' ')
    .map(item => (exceptions.includes(item) ? item : item.slice(0, 1).toUpperCase() + item.slice(1)));
  return wordsArray.join(' ');
};
